import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import BackIcon from '@mui/icons-material/KeyboardArrowLeft';
import SearchIcon from '@mui/icons-material/Search';
import Typography from '@mui/material/Typography';
import { getCallers } from 'services/ivr.service';
import useRequest from 'hooks/useRequest';
import TextField from 'components/form/TextInput';
import Checkbox from 'components/form/Checkbox';
import { stringAvatar } from 'utils';
import Avatar from '@mui/material/Avatar';
import Button from 'components/Button';
import { callAddPerson } from 'services/ivr.service';
import toast from 'utils/toast';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';

const AddPerson = ({ onClose, onAdded, callSid }) => {
  const account = useSelector((s) => s.settings.account);
  const [callers, { loading }] = useRequest(getCallers, {
    account_id: account.account_id,
  });
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState({});

  const onSelect = async (e) => {
    const { value, checked } = e.target;
    setSelected({
      ...selected,
      [value]: checked,
    });
  };

  const onAddSelected = async () => {
    try {
      const selectedCallers = Object.keys(selected).filter(
        (key) => selected[key],
      );
      await Promise.all(
        selectedCallers.map((hash) => {
          return callAddPerson(hash, callSid, true);
        }),
      );
      toast.success('People added successfully');
      onClose();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const items = callers
    ?.map(({ user, hash }) => {
      const name = `${user.user_first_name} ${user.user_last_name}`;
      return {
        name,
        hash,
        user_mobile: user.user_mobile,
      };
    })
    .filter(({ name }) => name.toLowerCase().includes(query.toLowerCase()))
    .map(({ name, hash, user_mobile }) => (
      <ListItem key={hash}>
        <ListItemAvatar sx={{ minWidth: 45 }}>
          <Avatar
            {...stringAvatar(name, { width: 36, height: 36, fontSize: 24 })}
          />
        </ListItemAvatar>
        <ListItemText
          primary={name}
          primaryTypographyProps={{
            fontWeight: 500,
          }}
        />
        <ListItemSecondaryAction>
          <Checkbox checked={selected[hash]} value={hash} onChange={onSelect} />
        </ListItemSecondaryAction>
      </ListItem>
    ));

  const count = Object.values(selected).filter(Boolean).length;

  return (
    <Box display="flex" flexDirection="column" height={500} overflow="hidden">
      <Stack
        px={2}
        py={1}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          bgcolor: 'background.default',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Button
          size="small"
          onClick={onClose}
          variant="text"
          startIcon={<BackIcon />}
        >
          Back
        </Button>
        <Typography color="text.secondary">duration</Typography>
      </Stack>
      <Stack px={2} py={1}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography
            variant="subtitle1"
            color="text.secondary"
            fontWeight={600}
          >
            Add People
          </Typography>
          <Typography color="text.secondary">Max 3</Typography>
        </Stack>
        <TextField
          fullWidth
          value={query}
          size="small"
          margin="none"
          onChange={(e) => setQuery(e.target.value)}
          startAdornment={
            <SearchIcon fontSize="small" color="text.secondary" />
          }
          placeholder="Start typing name..."
        />
      </Stack>
      <List disablePadding sx={{ overflow: 'auto', flex: 1 }}>
        {items}
      </List>
      <Box
        p={1.5}
        sx={{
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: 'background.default',
        }}
      >
        <Button
          fullWidth
          disabled={count === 0 || count > 3}
          onClick={onAddSelected}
        >
          Add People
        </Button>
      </Box>
    </Box>
  );
};

export default AddPerson;
